import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import MagicButton from 'components/MagicButton/MagicButton';
import { CharacterCounter } from 'components/CharacterCounter/CharacterCounter';
import { AssetUrls } from 'config/assets';
import { useManageUserEliteMutation } from 'store/services/admin/adminApi';
import { EliteStatusInfo, EliteDowngradeTiming } from 'types/interfaces/admin/userDetails';
import './RevokeEliteModal.css';

interface RevokeEliteModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: number;
  username: string;
  eliteInfo: EliteStatusInfo;
  onSuccess: () => void;
}

const RevokeEliteModal: React.FC<RevokeEliteModalProps> = ({
  isOpen,
  onClose,
  userId,
  username,
  eliteInfo,
  onSuccess,
}) => {
  const hasStripe = eliteInfo.stripeSubscriptionStatus === 'active';
  const [timing, setTiming] = useState<EliteDowngradeTiming>(
    hasStripe ? 'end_of_cycle' : 'immediate'
  );
  const [specificDate, setSpecificDate] = useState<string>('');
  const [cancelStripe, setCancelStripe] = useState<boolean>(true);
  const [note, setNote] = useState<string>('');
  const [confirmed, setConfirmed] = useState<boolean>(false);

  const [manageElite, { isLoading }] = useManageUserEliteMutation();

  useEffect(() => {
    if (!isOpen) {
      setTiming(hasStripe ? 'end_of_cycle' : 'immediate');
      setSpecificDate('');
      setCancelStripe(true);
      setNote('');
      setConfirmed(false);
    }
  }, [isOpen, hasStripe]);

  if (!isOpen) return null;

  const isSpecificDateValid = timing !== 'specific_date' || specificDate.length > 0;

  const handleSubmit = async () => {
    if (!confirmed) {
      setConfirmed(true);
      return;
    }

    try {
      await manageElite({
        userId,
        body: {
          action: 'downgrade',
          timing,
          date:
            timing === 'specific_date' && specificDate
              ? new Date(specificDate).toISOString()
              : undefined,
          cancelStripe: hasStripe ? cancelStripe : undefined,
          note: note.trim() || undefined,
        },
      }).unwrap();

      toast.success(`Elite status removed from ${username}.`);
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Failed to revoke elite:', error);
      toast.error(error.data?.message || 'Failed to revoke elite status. Please try again.');
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '\u2014';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getGrantedByLabel = () => {
    if (eliteInfo.grantedBy === 'admin' && hasStripe) return 'Stripe + Admin grant';
    if (eliteInfo.grantedBy === 'stripe') return 'Stripe subscription';
    if (eliteInfo.grantedBy === 'admin') return 'Admin grant';
    return '\u2014';
  };

  const getExpiresLabel = () => {
    if (eliteInfo.expiresAt) return formatDate(eliteInfo.expiresAt);
    if (hasStripe && eliteInfo.currentPeriodEnd) {
      return `End of billing cycle (${formatDate(eliteInfo.currentPeriodEnd)})`;
    }
    return 'Indefinite';
  };

  const timingLabel = (t: EliteDowngradeTiming): string => {
    if (t === 'end_of_cycle') return 'end of billing cycle';
    if (t === 'immediate') return 'immediately';
    return `on ${formatDate(specificDate || null)}`;
  };

  const todayStr = new Date().toISOString().split('T')[0];

  const modalContent = (
    <div className="revoke-elite-modal-overlay" onClick={onClose}>
      <div className="revoke-elite-modal" onClick={(e) => e.stopPropagation()}>
        <div className="revoke-elite-modal-header">
          <h2 className="revoke-elite-modal-title">Remove Elite from {username}</h2>
          <button className="revoke-elite-close-button" onClick={onClose}>
            <img
              src={AssetUrls.icons.dropdowns.close}
              alt="Close"
              style={{ width: 16, height: 16 }}
            />
          </button>
        </div>

        <div className="revoke-elite-modal-content">
          {/* Current Elite Context */}
          <div className="revoke-elite-context-section">
            <div className="revoke-elite-info-row">
              <span className="revoke-elite-info-label">Granted by:</span>
              <span className="revoke-elite-info-value">{getGrantedByLabel()}</span>
            </div>
            <div className="revoke-elite-info-row">
              <span className="revoke-elite-info-label">Elite since:</span>
              <span className="revoke-elite-info-value">{formatDate(eliteInfo.eliteSince)}</span>
            </div>
            <div className="revoke-elite-info-row">
              <span className="revoke-elite-info-label">Expires:</span>
              <span className="revoke-elite-info-value">{getExpiresLabel()}</span>
            </div>
            {eliteInfo.adminNote && (
              <div className="revoke-elite-info-row">
                <span className="revoke-elite-info-label">Admin note:</span>
                <span className="revoke-elite-info-value revoke-elite-note-value">
                  {eliteInfo.adminNote}
                </span>
              </div>
            )}
          </div>

          {/* Timing Selector */}
          <div className="revoke-elite-form-group">
            <label className="revoke-elite-form-label">When to Remove Elite</label>
            <div className="revoke-elite-timing-options">
              {hasStripe && (
                <label className="revoke-elite-timing-option">
                  <input
                    type="radio"
                    name="timing"
                    value="end_of_cycle"
                    checked={timing === 'end_of_cycle'}
                    onChange={() => { setTiming('end_of_cycle'); setConfirmed(false); }}
                    disabled={isLoading}
                  />
                  <span className="revoke-elite-timing-label">End of Billing Cycle</span>
                  {eliteInfo.currentPeriodEnd && (
                    <span className="revoke-elite-timing-date">
                      ({formatDate(eliteInfo.currentPeriodEnd)})
                    </span>
                  )}
                </label>
              )}
              <label className="revoke-elite-timing-option">
                <input
                  type="radio"
                  name="timing"
                  value="immediate"
                  checked={timing === 'immediate'}
                  onChange={() => { setTiming('immediate'); setConfirmed(false); }}
                  disabled={isLoading}
                />
                <span className="revoke-elite-timing-label">Immediately</span>
              </label>
              <label className="revoke-elite-timing-option">
                <input
                  type="radio"
                  name="timing"
                  value="specific_date"
                  checked={timing === 'specific_date'}
                  onChange={() => { setTiming('specific_date'); setConfirmed(false); }}
                  disabled={isLoading}
                />
                <span className="revoke-elite-timing-label">Specific Date</span>
              </label>
            </div>
            {timing === 'specific_date' && (
              <input
                type="date"
                className="revoke-elite-form-input revoke-elite-date-input"
                style={{ marginTop: 12 }}
                value={specificDate}
                min={todayStr}
                onChange={(e) => { setSpecificDate(e.target.value); setConfirmed(false); }}
                disabled={isLoading}
              />
            )}
          </div>

          {/* Stripe cancellation checkbox */}
          {hasStripe && (
            <div className="revoke-elite-form-group">
              <label className="revoke-elite-stripe-checkbox-label">
                <input
                  type="checkbox"
                  checked={cancelStripe}
                  onChange={(e) => { setCancelStripe(e.target.checked); setConfirmed(false); }}
                  disabled={isLoading}
                  className="revoke-elite-stripe-checkbox"
                />
                <span>Also cancel the Stripe subscription</span>
              </label>
              {!cancelStripe && (
                <div className="revoke-elite-stripe-warning">
                  The user's Stripe subscription will remain active and they may
                  retain Elite benefits until the next billing cycle.
                </div>
              )}
            </div>
          )}

          {/* Admin note */}
          <div className="revoke-elite-form-group">
            <label htmlFor="revokeNote" className="revoke-elite-form-label">
              Admin Note
              <span className="revoke-elite-label-hint"> (optional)</span>
            </label>
            <textarea
              id="revokeNote"
              className="revoke-elite-form-textarea"
              placeholder="Reason for removal (e.g., Terms violation, Billing issue)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              maxLength={500}
              disabled={isLoading}
            />
            {note.length > 0 && (
              <CharacterCounter
                currentLength={note.length}
                maxLength={500}
                warningThreshold={450}
              />
            )}
          </div>

          {/* Confirmation step */}
          {confirmed && (
            <div className="revoke-elite-danger-banner">
              <strong>Confirm:</strong> This will remove Elite status from{' '}
              <strong>{username}</strong> {timingLabel(timing)}.
              {hasStripe && cancelStripe && ' The Stripe subscription will also be canceled.'}
              {' '}Are you sure?
            </div>
          )}
        </div>

        <div className="revoke-elite-modal-footer">
          <MagicButton
            label="Cancel"
            color="grey"
            onPress={onClose}
            isDisabled={isLoading}
            fullWidth={false}
          />
          <MagicButton
            label={
              isLoading
                ? 'Removing Elite...'
                : confirmed
                  ? 'Yes, Remove Elite'
                  : 'Remove Elite'
            }
            color="default"
            onPress={handleSubmit}
            isLoading={isLoading}
            isDisabled={isLoading || !isSpecificDateValid}
            fullWidth={false}
          />
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default RevokeEliteModal;
